import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Users, Download, Search } from "lucide-react";

function toCsv(customers) {
  const rows = [["Name", "Email", "Mobile", "Vouchers", "Total spent (£)", "Last purchase"]];
  customers.forEach((c) => rows.push([
    c.name, c.email, c.phone, c.voucher_count, c.total_spent.toFixed(2), String(c.last_purchase || "").slice(0, 10),
  ]));
  return rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function CustomersTab() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/business/customers").then(({ data }) => setCustomers(data.customers)).catch(() => setCustomers([]));
  }, []);

  if (!customers) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const filtered = customers.filter((c) =>
    `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  const totalRevenue = customers.reduce((s, c) => s + c.total_spent, 0);

  return (
    <div className="space-y-5" data-testid="customers-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          <div className="flex items-center gap-2 text-[#8a97a8] text-sm"><Users size={16} /> Total customers</div>
          <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="customers-count">{customers.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          <div className="text-[#8a97a8] text-sm">Lifetime revenue</div>
          <p className="text-2xl font-bold text-[#16243b] mt-1">£{totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          <div className="text-[#8a97a8] text-sm">Avg. per customer</div>
          <p className="text-2xl font-bold text-[#16243b] mt-1">£{customers.length ? (totalRevenue / customers.length).toFixed(2) : "0.00"}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-[360px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97a8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or mobile…" data-testid="customers-search"
            className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        </div>
        <button onClick={() => download("customers.csv", toCsv(customers))} disabled={customers.length === 0}
          data-testid="customers-export-btn" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f6f8fb] text-[#8a97a8]">
            <tr>{["Customer", "Email", "Mobile", "Vouchers", "Total spent", "Last purchase"].map((h) => <th key={h} className="text-left px-5 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.email} className="border-t border-gray-50" data-testid={`customer-row-${c.email}`}>
                <td className="px-5 py-3 font-medium text-[#16243b] whitespace-nowrap">{c.name || "—"}</td>
                <td className="px-5 py-3 text-[#5a6b80] whitespace-nowrap">{c.email}</td>
                <td className="px-5 py-3 text-[#5a6b80] whitespace-nowrap">{c.phone || "—"}</td>
                <td className="px-5 py-3 text-[#5a6b80]">{c.voucher_count}</td>
                <td className="px-5 py-3 font-semibold text-[#16243b]">£{c.total_spent.toFixed(2)}</td>
                <td className="px-5 py-3 text-[#5a6b80] whitespace-nowrap">{String(c.last_purchase || "").slice(0, 10) || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#8a97a8]">{customers.length === 0 ? "No customers yet — sales will appear here." : "No matches."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
